'use client';

import React, { useState, FormEvent } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

import { getStripe } from '@/lib/stripe/client';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Product Catalog ────────────────────────────────────────────────────────

export interface PricingProduct {
  id: string;
  name: string;
  price: number; // USD
  description: string;
  badge?: string;
  features: string[];
  category: 'ad-space' | 'vip' | 'featured';
}

export const PRICING_PRODUCTS: PricingProduct[] = [
  // ── Ad Space ──────────────────────────────────────────────────────────────
  {
    id: 'banner-ad-weekly',
    name: 'Banner Ad — Weekly',
    price: 25,
    description: 'Display banner ad across ViddStarTV for 7 days.',
    category: 'ad-space',
    features: [
      'Homepage banner placement',
      'Runs for 7 days',
      'Appears on all pages',
      'Link to your site',
    ],
  },
  {
    id: 'banner-ad-monthly',
    name: 'Banner Ad — Monthly',
    price: 75,
    description: 'Display banner ad across ViddStarTV for 30 days.',
    badge: 'Best Value',
    category: 'ad-space',
    features: [
      'Homepage banner placement',
      'Runs for 30 days',
      'Appears on all pages',
      'Link to your site',
      'Priority placement',
    ],
  },
  {
    id: 'video-ad-weekly',
    name: 'Video Ad — Weekly',
    price: 50,
    description: 'Pre-roll or mid-roll video ad shown before/during videos for 7 days.',
    category: 'ad-space',
    features: [
      'Pre-roll or mid-roll placement',
      'Runs for 7 days',
      'Skippable after 5 seconds',
      'Full analytics dashboard',
    ],
  },
  {
    id: 'video-ad-monthly',
    name: 'Video Ad — Monthly',
    price: 150,
    description: 'Pre-roll or mid-roll video ad shown before/during videos for 30 days.',
    badge: 'Popular',
    category: 'ad-space',
    features: [
      'Pre-roll or mid-roll placement',
      'Runs for 30 days',
      'Skippable after 5 seconds',
      'Full analytics dashboard',
      'Priority rotation',
    ],
  },
  // ── Featured Video Placement ───────────────────────────────────────────────
  {
    id: 'featured-video-weekly',
    name: 'Featured Video Placement — Weekly',
    price: 50,
    description: 'Pin your video in the Featured Video spotlight on the homepage for 7 days.',
    badge: 'New',
    category: 'featured',
    features: [
      'Homepage Featured Video section',
      'Pinned for 7 days',
      'Prominent hero placement',
      'Drives maximum views',
      'Shown to all visitors',
    ],
  },
  // ── VIP Products ──────────────────────────────────────────────────────────
  {
    id: 'vip-creator',
    name: 'VIP Creator',
    price: 29,
    description: 'Unlock VIP Creator status — priority video review, verified badge, and more.',
    badge: 'VIP',
    category: 'vip',
    features: [
      'Priority video review (24h)',
      'VIP verified badge on profile',
      'Submit up to 10 videos/month',
      'Early access to new features',
    ],
  },
  {
    id: 'vip-creator-pro',
    name: 'VIP Creator Pro',
    price: 79,
    description: 'Full VIP Pro package — everything in VIP Creator plus homepage spotlight.',
    badge: 'VIP PRO',
    category: 'vip',
    features: [
      'Everything in VIP Creator',
      'Monthly homepage spotlight',
      'Unlimited video submissions',
      'Dedicated support',
      'Revenue share eligibility',
    ],
  },
];

// ─── Payment Form ────────────────────────────────────────────────────────────

interface PaymentFormProps {
  clientSecret: string;
  product: PricingProduct;
  onSuccess: () => void;
  onCancel: () => void;
}

function PaymentForm({ clientSecret, product, onSuccess, onCancel }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [payError, setPayError] = useState('');
  const supabase = createClient();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);
    setPayError('');

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}` },
      redirect: 'if_required',
    });

    if (error) {
      setPayError(error.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      const { error: confirmError } = await supabase.functions.invoke('confirm-payment', {
        body: { paymentIntentId: paymentIntent.id, tableName: 'subscriptions' },
      });
      if (confirmError) {
        console.error('Confirm payment error:', confirmError);
      }
      onSuccess();
    }
    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-surface-tertiary border border-stroke-primary rounded-[4px] p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-text-primary font-bold text-sm">{product.name}</span>
          <span className="text-brand-red font-bold text-lg">${product.price}</span>
        </div>
        <p className="text-text-muted text-xs">{product.description}</p>
      </div>

      <PaymentElement />

      {payError && (
        <p className="text-red-400 text-sm border border-red-400 border-opacity-30 bg-red-400 bg-opacity-10 rounded-[4px] px-3 py-2">
          {payError}
        </p>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="flex-1 border border-stroke-primary text-text-secondary hover:text-text-primary hover:border-stroke-light text-sm font-bold uppercase px-4 py-2.5 rounded-[4px] transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 btn-primary text-sm px-4 py-2.5 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Processing...
            </span>
          ) : (
            `Pay $${product.price}`
          )}
        </button>
      </div>
    </form>
  );
}

// ─── Checkout Step ───────────────────────────────────────────────────────────

interface CheckoutStepProps {
  product: PricingProduct;
  onSuccess: () => void;
  onBack: () => void;
}

function CheckoutStep({ product, onSuccess, onBack }: CheckoutStepProps) {
  const { user, profile } = useAuth();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Guest / auth form fields
  const [firstName, setFirstName] = useState(() => {
    if (profile?.full_name) return profile.full_name.split(' ')[0] || '';
    return '';
  });
  const [lastName, setLastName] = useState(() => {
    if (profile?.full_name) {
      const parts = profile.full_name.split(' ');
      return parts.slice(1).join(' ') || '';
    }
    return '';
  });
  const [email, setEmail] = useState(user?.email || '');
  const [videoId, setVideoId] = useState('');

  const supabase = createClient();

  const handleInitPayment = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const additionalFields: Record<string, unknown> = { plan: product.id };
      if (product.id === 'featured-video-weekly' && videoId.trim()) {
        additionalFields.video_id = videoId.trim();
      }

      const { data, error: fnError } = await supabase.functions.invoke<{
        clientSecret: string;
        recordId: string;
      }>('create-payment-intent', {
        body: {
          paymentData: {
            amount: product.price,
            currency: 'usd',
            tableName: 'subscriptions',
            description: product.name,
            additionalFields,
          },
          customerInfo: {
            userId: user?.id ?? null,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            stripeCustomerId: (profile as any)?.stripe_customer_id ?? null,
          },
        },
      });

      if (fnError) {
        throw new Error((data as any)?.error ?? fnError.message ?? 'Payment setup failed');
      }
      if (!data?.clientSecret) throw new Error('No client secret returned');
      setClientSecret(data.clientSecret);
    } catch (err: any) {
      setError(err.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const stripePromise = getStripe();

  if (clientSecret) {
    return (
      <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' } }}>
        <PaymentForm
          clientSecret={clientSecret}
          product={product}
          onSuccess={onSuccess}
          onCancel={() => setClientSecret(null)}
        />
      </Elements>
    );
  }

  return (
    <form onSubmit={handleInitPayment} className="space-y-4">
      <div className="bg-surface-tertiary border border-stroke-primary rounded-[4px] p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-text-primary font-bold text-sm">{product.name}</span>
          <span className="text-brand-red font-bold text-lg">${product.price}</span>
        </div>
        <p className="text-text-muted text-xs">{product.description}</p>
      </div>

      {product.id === 'featured-video-weekly' && (
        <div>
          <label className="block text-text-secondary text-xs font-bold uppercase tracking-wide mb-1">
            Video ID to Feature *
          </label>
          <input
            type="text"
            required
            value={videoId}
            onChange={e => setVideoId(e.target.value)}
            className="w-full bg-surface-tertiary border border-stroke-primary text-text-primary text-sm px-3 py-2 rounded-[4px] focus:outline-none focus:border-brand-red"
            placeholder="Enter the Video ID from your ViddStar video URL"
          />
          <p className="text-text-muted text-xs mt-1">Find your Video ID in the URL when viewing your video (e.g. /video/abc123)</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-text-secondary text-xs font-bold uppercase tracking-wide mb-1">
            First Name *
          </label>
          <input
            type="text"
            required
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            className="w-full bg-surface-tertiary border border-stroke-primary text-text-primary text-sm px-3 py-2 rounded-[4px] focus:outline-none focus:border-brand-red"
            placeholder="John"
          />
        </div>
        <div>
          <label className="block text-text-secondary text-xs font-bold uppercase tracking-wide mb-1">
            Last Name *
          </label>
          <input
            type="text"
            required
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            className="w-full bg-surface-tertiary border border-stroke-primary text-text-primary text-sm px-3 py-2 rounded-[4px] focus:outline-none focus:border-brand-red"
            placeholder="Doe"
          />
        </div>
      </div>

      <div>
        <label className="block text-text-secondary text-xs font-bold uppercase tracking-wide mb-1">
          Email Address *
        </label>
        <input
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="w-full bg-surface-tertiary border border-stroke-primary text-text-primary text-sm px-3 py-2 rounded-[4px] focus:outline-none focus:border-brand-red"
          placeholder="you@example.com"
        />
      </div>

      {error && (
        <p className="text-red-400 text-sm border border-red-400 border-opacity-30 bg-red-400 bg-opacity-10 rounded-[4px] px-3 py-2">
          {error}
        </p>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 border border-stroke-primary text-text-secondary hover:text-text-primary hover:border-stroke-light text-sm font-bold uppercase px-4 py-2.5 rounded-[4px] transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 btn-primary text-sm px-4 py-2.5 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Setting up...
            </span>
          ) : (
            'Continue to Payment'
          )}
        </button>
      </div>
    </form>
  );
}

// ─── Main Pricing Modal ──────────────────────────────────────────────────────

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultCategory?: 'ad-space' | 'vip' | 'featured' | 'all';
}

const CATEGORY_LABELS: Record<string, string> = {
  all: 'All Plans',
  'ad-space': 'Ad Space',
  featured: 'Featured Video',
  vip: 'VIP',
};

export default function PricingModal({ isOpen, onClose, defaultCategory = 'all' }: PricingModalProps) {
  const [activeCategory, setActiveCategory] = useState<string>(defaultCategory);
  const [selectedProduct, setSelectedProduct] = useState<PricingProduct | null>(null);
  const [step, setStep] = useState<'browse' | 'checkout' | 'success'>('browse');

  const filteredProducts =
    activeCategory === 'all'
      ? PRICING_PRODUCTS
      : PRICING_PRODUCTS.filter(p => p.category === activeCategory);

  const handleSelect = (product: PricingProduct) => {
    setSelectedProduct(product);
    setStep('checkout');
  };

  const handleSuccess = () => {
    setStep('success');
  };

  const handleClose = () => {
    setStep('browse');
    setSelectedProduct(null);
    setActiveCategory(defaultCategory);
    onClose();
  };

  if (!isOpen) return null;

  const categoryBadgeColor: Record<string, string> = {
    'ad-space': 'bg-blue-500',
    featured: 'bg-yellow-500',
    vip: 'bg-purple-500',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-80" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-surface-primary border border-stroke-primary rounded-[4px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stroke-primary flex-shrink-0">
          <div>
            {step === 'browse' && (
              <div className="flex items-baseline gap-2">
                <span className="text-text-primary text-xl font-normal uppercase">ADVERTISE</span>
                <span className="text-brand-red font-bold text-xl uppercase">& PROMOTE</span>
              </div>
            )}
            {step === 'checkout' && selectedProduct && (
              <div className="flex items-baseline gap-2">
                <span className="text-text-primary text-xl font-normal uppercase">CHECKOUT</span>
              </div>
            )}
            {step === 'success' && (
              <div className="flex items-baseline gap-2">
                <span className="text-brand-red font-bold text-xl uppercase">PAYMENT SUCCESSFUL</span>
              </div>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-text-muted hover:text-text-primary transition-colors p-1"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* ── Browse Step ── */}
          {step === 'browse' && (
            <>
              {/* Category tabs */}
              <div className="flex gap-2 mb-5 flex-wrap">
                {(['all', 'ad-space', 'featured', 'vip'] as const).map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-[4px] border transition-colors ${
                      activeCategory === cat
                        ? 'bg-brand-red border-brand-red text-white' :'border-stroke-primary text-text-secondary hover:border-stroke-light hover:text-text-primary'
                    }`}
                  >
                    {CATEGORY_LABELS[cat]}
                  </button>
                ))}
              </div>

              {/* Product grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {filteredProducts.map(product => (
                  <div
                    key={product.id}
                    className="border border-stroke-primary bg-surface-secondary hover:border-brand-red transition-colors duration-200 rounded-[4px] p-4 flex flex-col"
                  >
                    {/* Badge + name */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        {product.badge && (
                          <span
                            className={`inline-block text-white text-xs font-bold uppercase px-2 py-0.5 rounded-[2px] mb-1 ${
                              categoryBadgeColor[product.category] || 'bg-brand-red'
                            }`}
                          >
                            {product.badge}
                          </span>
                        )}
                        <h3 className="text-text-primary font-bold text-sm leading-snug">{product.name}</h3>
                      </div>
                      <span className="text-brand-red font-bold text-xl flex-shrink-0">${product.price}</span>
                    </div>

                    <p className="text-text-muted text-xs mb-3 leading-relaxed">{product.description}</p>

                    {/* Features */}
                    <ul className="space-y-1 mb-4 flex-1">
                      {product.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-text-secondary">
                          <svg className="w-3.5 h-3.5 text-brand-red flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {f}
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => handleSelect(product)}
                      className="w-full btn-primary text-sm py-2"
                    >
                      Get Started — ${product.price}
                    </button>
                  </div>
                ))}
              </div>

              <p className="text-text-muted text-xs text-center mt-5">
                Secure payments powered by Stripe. All prices in USD.
              </p>
            </>
          )}

          {/* ── Checkout Step ── */}
          {step === 'checkout' && selectedProduct && (
            <CheckoutStep
              product={selectedProduct}
              onSuccess={handleSuccess}
              onBack={() => setStep('browse')}
            />
          )}

          {/* ── Success Step ── */}
          {step === 'success' && selectedProduct && (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-16 h-16 bg-green-500 bg-opacity-20 border border-green-500 border-opacity-40 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-text-primary font-bold text-xl mb-2">Payment Successful!</h3>
              <p className="text-text-secondary text-sm mb-1">
                Thank you for purchasing <strong className="text-text-primary">{selectedProduct.name}</strong>.
              </p>
              <p className="text-text-muted text-xs mb-6">
                A confirmation will be sent to your email. Our team will activate your placement shortly.
              </p>
              <button onClick={handleClose} className="btn-primary px-8 py-2.5">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
